import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const BiographySection = () => {
  const biography = {
    intro: "I'm Edrian Bass, a multimedia professional and multifaceted entrepreneur bridging the worlds of technology, business, and creative media. With over 8 years of experience in IT engineering and extensive expertise in multimedia production, I've built a career on transforming complex technical challenges into elegant solutions while managing large-scale media operations.",
    journey: "My journey began in Lagos, Nigeria, where I discovered my passion for technology and media at an early age. This passion evolved into a comprehensive skill set spanning full-stack development, cloud architecture, digital transformation, and multimedia production. As an entrepreneur, I've founded and scaled multiple successful ventures, including Ed Pulse, my creative direction brand that merges technical precision with artistic vision. Throughout my career, I've managed over 3 TV networks, bringing innovative content and technical excellence to audiences across multiple markets.",
    mission: "Currently, I serve as the manager of the Restored Woman TV Network, based in Atlanta, Georgia, United States of America. This role combines my technical expertise with my passion for empowering communities through media. I also dedicate myself to mentoring the next generation of tech professionals and entrepreneurs across Africa, helping young professionals navigate the intersection of technology, media, and entrepreneurship.",
    vision: "My vision extends beyond individual success—I'm committed to fostering a thriving tech and media ecosystem in Africa and beyond, where innovation meets opportunity, and where the next generation of leaders can build sustainable, impactful businesses that transform communities through technology and compelling storytelling."
  };

  const highlights = [
    {
      icon: "Code2",
      title: "Technical Excellence",
      description: "Specialized in React, Node.js, cloud architecture, and modern web technologies with proven track record of delivering scalable solutions."
    },
    {
      icon: "TrendingUp",
      title: "Business Acumen",
      description: "Founded multiple successful ventures with expertise in digital transformation, business strategy, and market expansion across African markets."
    },
    {
      icon: "Tv",
      title: "Multimedia Leadership",
      description: "Managed over 3 TV networks and currently managing Restored Woman TV Network in Atlanta, Georgia, USA, combining technical expertise with creative vision."
    },
    {
      icon: "Palette",
      title: "Creative Vision",
      description: "Ed Pulse brand combines technical expertise with creative direction, delivering compelling visual identities and brand experiences across media platforms."
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-card">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-12 lg:mb-16"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="BookOpen" size={24} color="var(--color-primary)" />
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold">My Story</h2>
          </div>

          <div className="space-y-6 text-base md:text-lg text-muted-foreground leading-relaxed">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              {biography?.intro}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              {biography?.journey}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              {biography?.mission}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              {biography?.vision}
            </motion.p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {highlights?.map((highlight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="bg-background rounded-xl lg:rounded-2xl p-6 lg:p-8 border border-border hover:border-primary/50 transition-colors"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Icon name={highlight?.icon} size={28} color="var(--color-primary)" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold mb-3">{highlight?.title}</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {highlight?.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BiographySection;